import { useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import { ItemTypes } from "../../../abstract/controller/enum/dnd/ItemTypes";
import { getStatusIdle, setIdle } from "../../../abstract/redux/slice/statusSlice";
import {
  handleChoiceRemove,
  handleConditionRemove,
  handleFunctionRemove,
  handleParameterRemove,
} from "../../../abstract/redux/slice/structureSlice";
import IconTrashOutline from "../../../themes/icons/IconTrashOutline";
import styles from "./FooterTrash.module.scss";
import DialogSelect from "../../ui/modal/DialogSelect";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { setWindowLock } from "../../../abstract/redux/slice/overlaySlice";
import { useEffect, useRef, useState } from "react";

function FooterTrash() {
  const _dispatch = useDispatch();
  const _isStatusIdle = useSelector(getStatusIdle);

  const [_showModalParamRef, _setShowModalParamRef] = useState(false);
  const [_showModalParamKeep, _setShowModalParamKeep] = useState(false);
  const [_showModalChoiceKeep, _setShowModalChoiceKeep] = useState(false);

  const nodeRef = useRef(undefined);

  //-------------------------------------------------------------------------------------------------

  const [{ _isOver, _node }, drop] = useDrop(() => ({
    accept: [
      ItemTypes.PARAMETER,
      ItemTypes.CHOICE,
      ItemTypes.FUNCTION,
      ItemTypes.EXPRESSION,
      ItemTypes.CONDITION,
    ],

    collect: (monitor) => ({
      _isOver: monitor.isOver(),
      _node: monitor.getItem()?.element,
    }),
    drop(item, monitor) {
      switch (monitor.getItemType()) {
        case ItemTypes.PARAMETER:
          if (nodeUtils.getIdReferences(item.element)?.length) {
            _setShowModalParamRef(true);
          } else if (nodeUtils.hasParameters(item.element)) {
            _setShowModalParamKeep(true);
          } else {
            _dispatch(handleParameterRemove(item.element.id));
          }
          break;
        case ItemTypes.CHOICE:
          if (nodeUtils.hasChoices(item.element)) {
            _setShowModalChoiceKeep(true);
          } else {
            _dispatch(handleChoiceRemove(item.element.id));
          }
          break;
        case ItemTypes.FUNCTION:
          _dispatch(handleFunctionRemove(item.element.id));
          break;
        case ItemTypes.CONDITION:
          _dispatch(handleConditionRemove(item.element.id));
          break;
        default:
          console.log(`Unknown item type ${monitor.getItemType()}!`);
      }

      _dispatch(setIdle());
    },
  }));

  useEffect(() => {
    if (_node) {
      nodeRef.current = _node;
    }
  }, [_node]);

  //-------------------------------------------------------------------------------------------------

  const getClass = () => {
    return `${styles["trash"]} ${_isOver ? styles["trash--over"] : ""} ${_isStatusIdle ? styles["trash--hidden"] : ""}`;
  };

  return (
    <div ref={drop} className={getClass()}>
      <IconTrashOutline />
      <WindowModalRemoveOptions
        node={nodeRef.current}
        showModalParamRef={_showModalParamRef}
        setShowModalParamRef={_setShowModalParamRef}
        showModalParamKeep={_showModalParamKeep}
        setShowModalParamKeep={_setShowModalParamKeep}
        showModalChoiceKeep={_showModalChoiceKeep}
        setShowModalChoiceKeep={_setShowModalChoiceKeep}
      />
    </div>
  );
}

function WindowModalRemoveOptions({
  node,
  showModalParamRef,
  setShowModalParamRef,
  showModalParamKeep,
  setShowModalParamKeep,
  showModalChoiceKeep,
  setShowModalChoiceKeep,
}) {
  const _dispatch = useDispatch();

  if (!node) {
    return;
  }

  const id = nodeUtils.getID(node);

  return (
    <>
      {showModalParamRef && (
        <DialogSelect
          textHeader="remove options"
          textBody={`The selected parameter is the target of '${
            nodeUtils.getIdReferences(node)?.length
          }' linked parameters. Decide what to do with them.`}
          actions={[
            {
              text: "delete",
              handler: () => {
                _dispatch(handleParameterRemove(id, false, true));
                _dispatch(setWindowLock(false));
                setShowModalParamRef(false);
              },
            },
            {
              text: "detach",
              handler: () => {
                _dispatch(handleParameterRemove(id, false, false));
                _dispatch(setWindowLock(false));
                setShowModalParamRef(false);
              },
            },
            {
              text: "cancel",
              handler: () => {
                _dispatch(setWindowLock(false));
                setShowModalParamRef(false);
              },
            },
          ]}
          cancel={() => {
            _dispatch(setWindowLock(false));
            setShowModalParamRef(false);
          }}
        />
      )}
      {showModalParamKeep && (
        <DialogSelect
          textHeader="remove options"
          textBody={`Decide what to do with nested parameters.`}
          actions={[
            {
              text: "delete",
              handler: () => {
                _dispatch(handleParameterRemove(id, false, false));
                _dispatch(setWindowLock(false));
                setShowModalParamKeep(false);
              },
            },
            {
              text: "keep",
              handler: () => {
                _dispatch(handleParameterRemove(id, true, false));
                _dispatch(setWindowLock(false));
                setShowModalParamKeep(false);
              },
            },
            {
              text: "cancel",
              handler: () => {
                _dispatch(setWindowLock(false));
                setShowModalParamKeep(false);
              },
            },
          ]}
          cancel={() => {
            _dispatch(setWindowLock(false));
            setShowModalParamKeep(false);
          }}
        />
      )}
      {showModalChoiceKeep && (
        <DialogSelect
        textHeader="remove options"
        textBody={`Decide what to do with nested choices.`}
        actions={[
          {
            text: "delete",
            handler: () => {
              _dispatch(handleChoiceRemove(id, false));
              _dispatch(setWindowLock(false));
              setShowModalChoiceKeep(false);
            },
          },
          {
            text: "keep",
            handler: () => {
              _dispatch(handleChoiceRemove(id, true));
              _dispatch(setWindowLock(false));
              setShowModalChoiceKeep(false);
            },
          },
        ]}
        cancel={() => {
          _dispatch(setWindowLock(false));
          setShowModalChoiceKeep(false);
        }}
      />
      )}
    </>
  );
}

export default FooterTrash;
